import { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import HTMLParser from "html-parse-stringify";
import {
    findNodeName,
    parseInlineCss,
    sanitizeHtml,
} from "../../utils/parser/library";
import log from "../../utils/logger/library";
import { makeAbsoluteCBCDomain } from "../../utils/path/library";
import { SECTIONS } from "../../constants/sections";
import { ESCAPED_CHARS } from "../../constants/html";
import useMounted from "../../hooks/useMounted";
import { SectionHeading } from "../Heading/component";

/**
 * Function representing the Customhtml component.
 *
 * This component parses and renders and custom html tokens
 */
const CustomHtml = (props) => {
    const [attributes, setAttributes] = useState({});
    const [elementHeight, setElementHeight] = useState(props.height || null);
    const baseSection = useSelector((state) => state.app.baseSection);
    const iframeRef = useRef(null);
    const intervalId = useRef(null);
    const isMounted = useMounted();

    const getAttributes = () => {
        let newAttributes = {};

        if (props.src) {
            newAttributes.width = "100%";
            newAttributes.src = makeAbsoluteCBCDomain(props.src);

            if (props.height) {
                newAttributes.height = props.height;
            }
        } else {
            newAttributes = attributesWithNoSource();
        }
        setAttributes(newAttributes);
    };

    const attributesWithNoSource = () => {
        let newAttributes = {};
        let parsedHtml;

        // Clean ESI declarations, the parser won't read esi:XXXXX properly, so transform it to just esi
        let optimizedHtml = props.html.replace(/esi:[a-zA-Z0-9]*/g, "esi");

        // Sanitize HTML as much as possible
        let sanitizedHtml = sanitizeHtml(optimizedHtml);
        // Need to wrap content into one element
        try {
            parsedHtml = HTMLParser.parse(`<span>${sanitizedHtml}</span>`);
        } catch (error) {
            parsedHtml = HTMLParser.parse(`<span></span>`);
            log.error("(CustomHtml) attributesWithNoSource", error);
        }

        let withIframe = findNodeName(parsedHtml[0], "iframe");
        let withEsi = findNodeName(parsedHtml[0], "esi");
        if (withIframe) {
            let array = withIframe[0].attrs;

            for (let element in array) {
                if (Object.prototype.hasOwnProperty.call(array, element)) {
                    newAttributes[element] = array[element];
                }
            }
            //Modify values
            newAttributes.width = "100%";

            if (newAttributes.style) {
                newAttributes.style = parseInlineCss(newAttributes.style);
            }

            if (newAttributes.src) {
                let unescapedUrl = newAttributes.src;

                ESCAPED_CHARS.forEach((char) => {
                    const [k, v] = char;
                    const regex = new RegExp(k, "gi");

                    unescapedUrl = unescapedUrl.replace(regex, v);
                });

                newAttributes.src = makeAbsoluteCBCDomain(unescapedUrl);
            }

            if (newAttributes.class) {
                delete newAttributes.class;
            }

            // https://developer.mozilla.org/en-US/docs/Web/HTML/Element/iframe#attributes
            if (newAttributes.allowfullscreen === "true") {
                newAttributes.allow = "fullscreen";
                delete newAttributes.allowfullscreen;
            }
        } else if (withEsi) {
            log.info("this embed has esi, here is the parsed obj: ", withEsi);
        } else {
            // do whenever there is no iframe, or ESI include
            newAttributes.width = "100%";
        }
        return newAttributes;
    };

    /** Update inner HTML content when props data changes
     *  Also hook interval timer to check changes in third party iframe content eg. height and links
     *  @returns {void}
     */
    const updateIframeNoSrcContent = () => {
        if (iframeRef.current?.src === "") {
            // Sanitize HTML as much as possible
            let sanitizedHtml = sanitizeHtml(props.html);
            const iframeDoc = iframeRef.current?.contentWindow?.document;

            if (iframeDoc) {
                iframeDoc.open();
                const customCSSPath =
                    baseSection && SECTIONS[baseSection]
                        ? `/a/styles/custom/${baseSection}Custom.css`
                        : null;

                if (customCSSPath) {
                    const customCSS = `<link type="text/css" href="${customCSSPath}" rel="stylesheet"/>`;
                    iframeDoc.write(customCSS);
                }

                // Prevents flash of undefined text
                if (sanitizedHtml === "undefined") {
                    sanitizedHtml = "";
                }

                iframeDoc.write(`<div>${sanitizedHtml}</div>`);
                iframeDoc.close();
            }

            const checkInterval = (targetParent) => {
                try {
                    // All links inside the iframe will open in current parent window when requested
                    // Wrap with checkInterval as we have no control of when iframe content is updated
                    const iframeLinks =
                        iframeDoc.documentElement.getElementsByTagName("a");
                    for (let link of iframeLinks) {
                        link.target =
                            targetParent || link.href ? "_parent" : "_self";
                    }

                    // Grab the html element because elements in the body with padding aren't always calculated
                    iframeDoc.body.style.overflow = "hidden";
                    iframeDoc.body.style.margin = "0px"; // Eliminates default browser margin which sometimes causes minor cut-offs

                    const itemHeight =
                        iframeRef.current?.contentWindow?.document?.body
                            ?.scrollHeight || null;
                    if (
                        itemHeight &&
                        itemHeight !== iframeRef.current?.clientHeight
                    ) {
                        let stringHeight = itemHeight.toString();

                        if (!props.height) {
                            const heightFromHtml = sanitizedHtml.match(
                                /height="([A-Za-z0-9]*)"/
                            );
                            stringHeight = heightFromHtml
                                ? heightFromHtml[1]
                                : 0;
                        }

                        const newHeight =
                            itemHeight ||
                            props.height ||
                            stringHeight.toString();

                        setElementHeight(newHeight);
                    }
                } catch (error) {
                    window.clearInterval(intervalId.current);
                    log.error(
                        `(CustomHtml) updateIframeNoSrcContent - Unable to get iframe height`,
                        error
                    );
                }
            };

            //Check height on interval
            let interval = 1000;
            window.clearInterval(intervalId.current);
            intervalId.current = window.setInterval(
                checkInterval,
                interval,
                props.targetParent
            );
        }
    };

    useEffect(() => {
        updateIframeNoSrcContent();
        getAttributes();
        return () => window.clearInterval(intervalId.current);
    }, []);

    useEffect(() => {
        if (!isMounted) return;
        updateIframeNoSrcContent();
    }, [props.html]);

    const heading = attributes.headingtitle && (
        <SectionHeading
            text={attributes.headingtitle}
            link={attributes.headinglink ? attributes.headinglink : null}
        />
    );

    if (props.loading) {
        attributes.loading = props.loading;
    }

    return (
        <>
            {heading}
            <iframe
                title={`${props.name ? props.name : ""} custom content`}
                className="customHtml"
                ref={iframeRef}
                height={elementHeight}
                {...attributes}
                data-testid="custom-html"
            />
        </>
    );
};

CustomHtml.propTypes = {
    src: PropTypes.string,
    height: PropTypes.string,
    html: PropTypes.string,
    name: PropTypes.string,
};

export default CustomHtml;
